import React from 'react';
import { Loader2 } from 'lucide-react';

interface RoutyMarkProps {
  /** Sizing classes, e.g. "w-7 h-7". */
  className?: string;
  /** @deprecated No longer rendered — the mark shows Routy's icon, not a
   * letter. Kept so existing call sites passing it don't need to change. */
  textClassName?: string;
  /** Show a spinner instead of the icon (e.g. while a turn is in flight). */
  busy?: boolean;
  /** Override the mark's background (e.g. "bg-transparent" when nested inside an already-dark button). */
  bgClassName?: string;
}

/**
 * Routy's brand mark: the Routy mascot icon, filled with a gradient so it
 * reads as "AI" at a glance. Reused everywhere Routy needs to identify
 * itself (launcher, panel header, assistant messages, AI-generated summary
 * cards) so the same mark is the one consistent "this is Routy" signal
 * across the UI.
 *
 * The icon art (`/routy-icon.png`) is a solid black glyph on a transparent
 * background, so it's colored via a CSS mask rather than an <img> — that
 * lets it pick up the gradient fill instead of rendering flat black.
 */
const RoutyMark: React.FC<RoutyMarkProps> = ({
  className = 'w-8 h-8',
  busy,
  bgClassName = 'bg-ink-950',
}) => (
  <div className={`${className} ${bgClassName} rounded-md flex items-center justify-center flex-shrink-0`}>
    {busy ? (
      <Loader2 className="w-1/2 h-1/2 text-white animate-spin" />
    ) : (
      <span
        className="w-5/6 h-5/6 bg-gradient-to-br from-sky-300 via-blue-400 to-indigo-400"
        style={{
          WebkitMaskImage: 'url(/routy-icon.png)',
          maskImage: 'url(/routy-icon.png)',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
        }}
        role="img"
        aria-label="Routy"
      />
    )}
  </div>
);

export default RoutyMark;
